import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { type SubmitEvent, useState } from 'react'

import type { PlatformUserSummary } from '@/bindings/PlatformUserSummary'
import { Button } from '@/components/button'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { platformUserKeys } from '@/features/platform-users/api'
import { FirstLoginHint } from '@/features/platform-users/first-login-hint'
import { emptyUserDraft, isEmailError, toUserRequest } from '@/features/platform-users/user-draft'
import { UserFields } from '@/features/platform-users/user-fields'
import { request } from '@/lib/api'
import { useT } from '@/lib/i18n/translate'

/**
 * No password field by design (E4): the backend seeds an undisclosed one and
 * the success panel tells the admin what to relay — the forgot-password URL.
 */
export function CreatePlatformUserPage() {
  const t = useT()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState(emptyUserDraft)
  const [created, setCreated] = useState<PlatformUserSummary | undefined>()

  const create = useMutation({
    mutationFn: () => request<PlatformUserSummary>('/platform/users', { method: 'POST', body: toUserRequest(draft) }),
    onSuccess: (user) => {
      queryClient.setQueryData(platformUserKeys.detail(user.id), user)
      setCreated(user)
      return queryClient.invalidateQueries({ queryKey: platformUserKeys.all })
    },
  })

  function submit(event: SubmitEvent) {
    event.preventDefault()
    create.mutate()
  }

  function another() {
    setCreated(undefined)
    setDraft(emptyUserDraft())
    create.reset()
  }

  if (created) {
    return (
      <Page width="sm" title={t('users.create')} className="flex flex-col gap-4">
        <Card className="flex flex-col gap-4">
          <p role="status" className="font-medium">
            {t('users.created', { email: created.email })}
          </p>
          <FirstLoginHint email={created.email} />
          <div className="flex flex-wrap gap-2">
            <Link
              to="/users/$userId"
              params={{ userId: created.id }}
              className="bg-accent text-accent-fg hover:bg-accent-hover flex min-h-tap items-center rounded-lg px-4 font-medium"
            >
              {t('users.openUser')}
            </Link>
            <Button variant="secondary" onClick={another}>
              {t('users.createAnother')}
            </Button>
          </div>
        </Card>
      </Page>
    )
  }

  const emailError = isEmailError(create.error) ? create.error : undefined

  return (
    <Page width="sm" title={t('users.create')} className="flex flex-col gap-4">
      <Card>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <p className="text-muted text-sm">{t('users.createIntro')}</p>
          <UserFields
            value={draft}
            onChange={(next) => {
              setDraft(next)
              if (emailError) create.reset()
            }}
            emailError={emailError}
          />
          <FormError error={emailError ? undefined : create.error} />
          <div className="flex gap-2">
            <Button type="submit" loading={create.isPending}>
              {t('users.create')}
            </Button>
            <Link to="/users" search={{ page: 1 }} className="text-accent-ink flex min-h-tap items-center px-3 font-medium hover:underline">
              {t('common.cancel')}
            </Link>
          </div>
        </form>
      </Card>
    </Page>
  )
}
